import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckSquare, Users, Trash2, Plus, Pencil, ArrowLeft, ArrowRight, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api/config';
import { sessionManager } from '../api/sessionManager';
import { employeeApi } from '../api/employeeApi';

const TasksPage = () => {
  const user = sessionManager.getUser();
  const isAdmin = user?.role === 'admin';

  const { employeeId } = useParams();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState(() => {
    const cached = sessionStorage.getItem('tasks_employees_cache');
    return cached ? JSON.parse(cached) : [];
  });
  const [tasks, setTasks] = useState(() => {
    const cached = sessionStorage.getItem('tasks_cache');
    return cached ? JSON.parse(cached) : [];
  });
  const [loading, setLoading] = useState(() => {
    return isAdmin ? !sessionStorage.getItem('tasks_employees_cache') : !sessionStorage.getItem('tasks_cache');
  });
  const [saving, setSaving] = useState(false);
  const [selectedViewEmployee, setSelectedViewEmployee] = useState(isAdmin && !employeeId ? null : user);
  const [employeeStats, setEmployeeStats] = useState(() => {
    const cached = sessionStorage.getItem('tasks_stats_cache');
    return cached ? JSON.parse(cached) : null;
  });
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [targetCount, setTargetCount] = useState(0);
  const [taskName, setTaskName] = useState('');
  const [description, setDescription] = useState('');
  const [taskDate, setTaskDate] = useState(getTodayDateString());
  
  // Edit Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTaskId, setEditTaskId] = useState(null);
  const [editTargetCount, setEditTargetCount] = useState(0);
  const [editTaskName, setEditTaskName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTaskDate, setEditTaskDate] = useState(getTodayDateString());

  function getTodayDateString() {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const fetchEmployees = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await employeeApi.getAll();
      if (res.success) {
        setEmployees(res.data);
        sessionStorage.setItem('tasks_employees_cache', JSON.stringify(res.data));
      }
    } catch (err) {
      console.error(err);
    }
  }, [isAdmin]);

  const fetchEmployeeData = useCallback(async (emp, showLoading = true) => {
    if (showLoading && !sessionStorage.getItem('tasks_cache')) setLoading(true);
    try {
      const [tasksRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/tasks.php?employee_id=${emp.id || emp.employee_id}`),
        fetch(`${API_BASE_URL}/employee_stats.php?employee_id=${emp.id || emp.employee_id}`)
      ]);
      const tasksJson = await tasksRes.json();
      const statsJson = await statsRes.json();
      
      if (tasksJson.success) {
        setTasks(tasksJson.data);
        sessionStorage.setItem('tasks_cache', JSON.stringify(tasksJson.data));
      }
      if (statsJson.success) {
        setEmployeeStats(statsJson.data);
        sessionStorage.setItem('tasks_stats_cache', JSON.stringify(statsJson.data));
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      if (!employeeId) {
        if (employees.length === 0) {
          setLoading(true);
          fetchEmployees().finally(() => setLoading(false));
        }
        setSelectedViewEmployee(null);
      } else {
        if (employees.length === 0) {
          fetchEmployees();
        }
        const existingEmp = employees.find(e => e.id.toString() === employeeId);
        if (existingEmp) {
          setSelectedViewEmployee(existingEmp);
        } else {
          setSelectedViewEmployee(prev => (prev && prev.id?.toString() === employeeId) ? prev : { id: employeeId, name: `Employee ${employeeId}` });
        }
      }
    } else {
      setSelectedViewEmployee(prev => (prev && prev.id === user?.id) ? prev : user);
    }
  }, [isAdmin, employeeId, employees, user, fetchEmployees]);

  useEffect(() => {
    if (selectedViewEmployee) {
      fetchEmployeeData(selectedViewEmployee, true);
      const interval = setInterval(() => {
        fetchEmployeeData(selectedViewEmployee, false);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedViewEmployee, fetchEmployeeData]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tasks.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: selectedEmployee,
          target_count: targetCount,
          task_name: taskName,
          description: description,
          task_date: taskDate
        })
      });
      const json = await res.json();
      if (json.success) {
        setShowModal(false);
        setTaskName('');
        setDescription('');
        setTargetCount(0);
        setTaskDate(getTodayDateString());
        if (selectedViewEmployee && selectedViewEmployee.id === parseInt(selectedEmployee)) {
          fetchEmployeeData(selectedViewEmployee, true);
        }
      } else {
        alert(json.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/tasks.php?id=${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success && selectedViewEmployee) fetchEmployeeData(selectedViewEmployee, true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditTask = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tasks.php?id=${editTaskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_count: editTargetCount,
          task_name: editTaskName,
          description: editDescription,
          task_date: editTaskDate
        })
      });
      const json = await res.json();
      if (json.success) {
        setShowEditModal(false);
        if (selectedViewEmployee) fetchEmployeeData(selectedViewEmployee, true);
      } else {
        alert(json.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (task) => {
    setEditTaskId(task.id);
    setEditTargetCount(task.target_count);
    setEditTaskName(task.task_name || '');
    setEditDescription(task.description || '');
    setEditTaskDate(task.task_date || getTodayDateString());
    setShowEditModal(true);
  };

  const getStatusBadge = (status) => {
    if (status === 'Completed') return <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold">{status}</span>;
    if (status === 'In Progress') return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-semibold">{status}</span>;
    return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs font-semibold">{status}</span>;
  };

  const renderDashboardCard = (title, count, value, subtitle, colorClass) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col items-center justify-center text-center">
      <h3 className="text-gray-500 font-semibold text-sm mb-1">{title}</h3>
      <div className={`text-4xl font-bold mb-2 ${colorClass}`}>{count}</div>
      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">{subtitle}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            {isAdmin && selectedViewEmployee && (
              <button 
                onClick={() => navigate('/dashboard/tasks')} 
                className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-600 transition-colors"
                title="Back to Employees"
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <h2 className="text-2xl font-bold text-gray-900">
              {!isAdmin ? 'My Tasks' : (selectedViewEmployee ? `${selectedViewEmployee.name || 'Employee'}'s Tasks` : 'Task Management')}
            </h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {!isAdmin ? 'Track your targets and progress.' : (selectedViewEmployee ? 'View employee performance and tasks.' : 'Select an employee to view their specific tasks and dashboard.')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedViewEmployee && (
             <button onClick={() => fetchEmployeeData(selectedViewEmployee, true)} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 bg-white">
               <RefreshCw size={18} className="text-gray-500" />
             </button>
          )}
          {isAdmin && (
            <button
              onClick={() => {
                if (selectedViewEmployee) setSelectedEmployee(selectedViewEmployee.id || '');
                setShowModal(true);
              }}
              className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 font-semibold shadow-sm"
            >
              <Plus size={18} /> Assign Task
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-500">
          <RefreshCw className="animate-spin mr-2" /> Loading...
        </div>
      ) : (
        isAdmin && !selectedViewEmployee ? (
          /* Employee List View (Admin Only) */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-left text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 font-semibold">Employee</th>
                  <th className="px-6 py-4 font-semibold">Email</th>
                  <th className="px-6 py-4 font-semibold">Role</th>
                  <th className="px-6 py-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {employees.map(emp => (
                  <tr key={emp.id} className="hover:bg-gray-50 group cursor-pointer transition-colors" onClick={() => navigate(`/dashboard/tasks/${emp.id}`)}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center font-bold text-lg">
                          {emp.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-gray-900 text-base">{emp.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500">{emp.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${emp.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {emp.role === 'admin' ? 'Admin' : 'Employee'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="text-primary-600 font-semibold hover:text-primary-800 flex items-center justify-end gap-1 w-full md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        View Tasks & Stats <ArrowRight size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {employees.length === 0 && (
                  <tr>
                    <td colSpan="4" className="py-12 text-center text-gray-500">No employees found.</td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          /* Employee Tasks & Stats View */
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Dashboard Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {renderDashboardCard('WIP-Login', employeeStats?.wip_login || 0, 'WIP-Login', 'Total Count', 'text-blue-600')}
              {renderDashboardCard('Disbursed', employeeStats?.disbursed || 0, 'Disbursed', 'Total Count', 'text-purple-600')}
              {renderDashboardCard('Hold', employeeStats?.hold || 0, 'Hold', 'Total Count', 'text-yellow-600')}
              {renderDashboardCard('Pending', employeeStats?.pending || 0, 'Pending', 'Total Count', 'text-orange-600')}
            </div>

            {/* Task Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(() => {
                const totalTarget = tasks.reduce((sum, t) => sum + parseInt(t.target_count || 0), 0);
                const totalCompleted = tasks.reduce((sum, t) => sum + parseInt(t.completed_count || 0), 0);
                const totalRemaining = Math.max(0, totalTarget - totalCompleted);
                return (
                  <>
                    {renderDashboardCard('Total Target', totalTarget, 'Target', 'Total Count', 'text-indigo-600')}
                    {renderDashboardCard('Total Completed', totalCompleted, 'Completed', 'Total Count', 'text-green-600')}
                    {renderDashboardCard('Total Remaining', totalRemaining, 'Remaining', 'Total Count', 'text-red-600')}
                  </>
                );
              })()}
            </div>

            {/* Tasks Table */}
            {(() => {
              const filteredTasks = tasks.filter(t => {
                const matchesSearch = (t.task_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                                      (t.description || '').toLowerCase().includes(searchQuery.toLowerCase());
                const matchesStatus = filterStatus === 'All' || t.status === filterStatus;
                return matchesSearch && matchesStatus;
              });
              const totalPages = Math.ceil(filteredTasks.length / rowsPerPage) || 1;
              const currentTasks = filteredTasks.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

              return tasks.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 py-20 text-center">
                  <CheckSquare className="mx-auto text-gray-300 mb-3" size={48} />
                  <p className="text-gray-500 font-medium">No tasks assigned yet.</p>
                  {isAdmin && (
                    <button onClick={() => { setSelectedEmployee(selectedViewEmployee.id); setShowModal(true); }} className="mt-4 text-primary-600 font-semibold hover:text-primary-700">
                      Assign a Task
                    </button>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <h3 className="font-bold text-gray-800">Assigned Tasks</h3>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                      <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                          type="text"
                          placeholder="Search tasks..."
                          value={searchQuery}
                          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                          className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow"
                        />
                      </div>
                      <div className="relative w-full sm:w-auto">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <select
                          value={filterStatus}
                          onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                          className="w-full sm:w-auto pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg appearance-none bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-shadow cursor-pointer"
                        >
                          <option value="All">All Statuses</option>
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto min-h-[250px]">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 font-semibold">Task Name</th>
                          <th className="px-6 py-3 font-semibold">Description</th>
                          <th className="px-6 py-3 font-semibold text-center">Target</th>
                          <th className="px-6 py-3 font-semibold text-center">Completed</th>
                          <th className="px-6 py-3 font-semibold text-center">Remaining</th>
                          <th className="px-6 py-3 font-semibold">Progress</th>
                          <th className="px-6 py-3 font-semibold">Status</th>
                          <th className="px-6 py-3 font-semibold">Task Date</th>
                          {isAdmin && <th className="px-6 py-3 font-semibold text-right">Actions</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {currentTasks.map((t) => {
                          const percent = Math.min(100, Math.round((t.completed_count / t.target_count) * 100));
                          const remaining = Math.max(0, t.target_count - t.completed_count);
                          return (
                            <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 font-bold text-gray-800">{t.task_name || '—'}</td>
                              <td className="px-6 py-4 text-gray-500 text-xs max-w-[200px] truncate" title={t.description}>
                                {t.description || '—'}
                              </td>
                              <td className="px-6 py-4 font-bold text-gray-800 text-center bg-gray-50/30">{t.target_count}</td>
                              <td className="px-6 py-4 text-green-600 font-bold text-center bg-green-50/30">{t.completed_count}</td>
                              <td className="px-6 py-4 text-orange-500 font-bold text-center bg-orange-50/30">{remaining}</td>
                              <td className="px-6 py-4 min-w-[150px]">
                                <div className="flex items-center gap-3">
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div className={`h-2 rounded-full ${percent === 100 ? 'bg-green-500' : 'bg-primary-500'}`} style={{ width: `${percent}%` }}></div>
                                  </div>
                                  <span className="text-xs font-bold text-gray-500 w-8">{percent}%</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">{getStatusBadge(t.status)}</td>
                              <td className="px-6 py-4 text-gray-500 text-xs font-medium whitespace-nowrap">{t.task_date}</td>
                              {isAdmin && (
                                <td className="px-6 py-4 text-right whitespace-nowrap">
                                  <button onClick={() => openEditModal(t)} className="text-blue-500 hover:text-blue-700 p-2 rounded-lg hover:bg-blue-50 transition-colors mr-1">
                                    <Pencil size={16} />
                                  </button>
                                  <button onClick={() => handleDeleteTask(t.id)} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors">
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                        {currentTasks.length === 0 && (
                          <tr>
                            <td colSpan={isAdmin ? 9 : 8} className="py-12 text-center text-gray-500">No tasks match your filters.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {/* Pagination Footer */}
                  <div className="border-t border-gray-100 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm text-gray-500 bg-white">
                    <div className="flex items-center gap-2">
                      <span>Rows per page:</span>
                      <div className="flex gap-1">
                        {[5, 10, 15, 20].map(n => (
                          <button
                            key={n}
                            onClick={() => { setRowsPerPage(n); setCurrentPage(1); }}
                            className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${rowsPerPage === n ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100'}`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span>
                        {filteredTasks.length === 0 ? '0' : Math.min((currentPage - 1) * rowsPerPage + 1, filteredTasks.length)}-
                        {Math.min(currentPage * rowsPerPage, filteredTasks.length)} of {filteredTasks.length}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages || totalPages === 0}
                          className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )
      )}

      {/* Create Task Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="font-bold text-lg text-gray-900">Assign New Task</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:bg-gray-200 p-1.5 rounded-lg transition-colors">✕</button>
            </div>
            <form onSubmit={handleCreateTask} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Select Employee *</label>
                <select required className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}>
                  <option value="">-- Choose Employee --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Name (Optional)</label>
                <input type="text" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" placeholder="e.g. Daily Calling" value={taskName} onChange={e => setTaskName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description (Optional)</label>
                <textarea rows="2" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none bg-gray-50 focus:bg-white" placeholder="e.g. Call fresh leads" value={description} onChange={e => setDescription(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Target *</label>
                  <input type="number" required min="0" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" value={targetCount} onChange={e => setTargetCount(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Date *</label>
                  <input type="date" required className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" value={taskDate} onChange={e => setTaskDate(e.target.value)} />
                </div>
              </div>
              <div className="pt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2">
                  {saving ? <RefreshCw className="animate-spin" size={16} /> : null}
                  {saving ? 'Saving...' : 'Assign Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 bg-gray-50/50">
              <h3 className="font-bold text-lg text-gray-900">Edit Task</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:bg-gray-200 p-1.5 rounded-lg transition-colors">✕</button>
            </div>
            <form onSubmit={handleEditTask} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Name (Optional)</label>
                <input type="text" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" placeholder="e.g. Daily Calling" value={editTaskName} onChange={e => setEditTaskName(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description (Optional)</label>
                <textarea rows="2" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none bg-gray-50 focus:bg-white" placeholder="e.g. Call fresh leads" value={editDescription} onChange={e => setEditDescription(e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Target *</label>
                  <input type="number" required min="0" className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" value={editTargetCount} onChange={e => setEditTargetCount(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Date *</label>
                  <input type="date" required className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500 transition-all bg-gray-50 focus:bg-white" value={editTaskDate} onChange={e => setEditTaskDate(e.target.value)} />
                </div>
              </div>
              <div className="pt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-sm disabled:opacity-50 transition-colors flex items-center gap-2">
                  {saving ? <RefreshCw className="animate-spin" size={16} /> : null}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
